import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PaytrieAPI } from '@paytrie/client'
import InteracLogo from '@/assets/interac-circle.png'
import { WidgetLayout } from '@/pages/Buy/WidgetLayout'
import { DarkModeMenu } from '@/panels/signup/DarkmodeSignUpPanel'
import './OrderStatus.css'
import { HybridWalletConnectButton } from '@/components/Button/HybridWalletConnect'
import { CopyIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons'
import {
  TRANSACTION_LISTENER_STORAGE_KEY,
  StoredTransaction,
} from '@/hooks/useTransactionListener'
import { WalletAddressQRCode } from '@/utils/WalletQrCode'
import { track } from '@/utils/analytics'
import { getCanSendFromWallet } from '@/utils/cadc'
import { isFiat, StableCoins } from '@/utils/currencies'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'

const OrderStatus = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const transactionId = searchParams.get('id')
  const parent = searchParams.get('parent')
  const [showExchangeModal, setShowExchangeModal] = useState(false)

  const [orderDetails, setOrderDetails] =
    useState<PaytrieAPI.Transaction | null>(null)
  const depositAddress = orderDetails?.depositAddress ?? null
  const canSendFromWallet = getCanSendFromWallet(
    orderDetails?.leftSideLabel ?? ''
  )
  const { enableRejectOldCadc } = useFeatureFlags()
  const showLegacyCadcSellHint =
    canSendFromWallet &&
    !enableRejectOldCadc &&
    orderDetails?.leftSideLabel?.startsWith(`${StableCoins.CADC}-`) === true
  const [error, setError] = useState<string | null>(null)
  const orderViewedRef = useRef<string | null>(null)
  const orderCompletedRef = useRef<string | null>(null)

  const fetchOrderDetails = useCallback(async (transactionId: string) => {
    try {
      setError(null)
      const response = await PaytrieAPI.getTransaction(transactionId)

      if (response.status === 200) {
        const data = response.data.data
        setOrderDetails(data)
        if (orderViewedRef.current !== transactionId) {
          orderViewedRef.current = transactionId
          track('order_status_viewed', {
            tx_id: transactionId,
            status: data?.status ?? null,
          })
        }
        if (
          data?.status?.toLowerCase().includes('complete') &&
          orderCompletedRef.current !== transactionId
        ) {
          orderCompletedRef.current = transactionId
          track('order_completed', { tx_id: transactionId })
        }
      } else {
        setError('notFound')
      }
    } catch {
      setError('failedToFetch')
    }
  }, [])

  const errorRef = useRef(error)
  errorRef.current = error

  useEffect(() => {
    if (!transactionId) return

    fetchOrderDetails(transactionId)

    const interval = setInterval(() => {
      if (errorRef.current !== 'notFound') {
        fetchOrderDetails(transactionId)
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [fetchOrderDetails, transactionId])

  const getStatusLabel = (status: string | null, orderId?: string) => {
    if (!status) return ''
    const lowerStatus = status.toLowerCase()

    // Check if transaction is completed in localStorage
    if (orderId) {
      const storedTransactions = getStoredTransactions()
      const storedTransaction = storedTransactions[orderId]
      if (
        storedTransaction?.status === 'completed' &&
        (lowerStatus.includes('pending') || lowerStatus.includes('waiting'))
      ) {
        return t('orderStatus.paymentCompleted')
      }
    }

    if (lowerStatus.includes('pending')) {
      return t('orderStatus.pendingPayment')
    } else if (lowerStatus.includes('complete')) {
      return t('orderStatus.complete')
    } else if (lowerStatus.includes('cancelled')) {
      return t('orderStatus.cancelled')
    } else if (lowerStatus.includes('expired')) {
      return t('orderStatus.expired')
    } else if (lowerStatus.includes('waiting')) {
      return '' //@ dev we don't want to display this
    } else if (lowerStatus.includes('processing')) {
      return t('orderStatus.processingRequest')
    } else if (lowerStatus.includes('sending')) {
      return t('orderStatus.sendingFunds')
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    }
  }

  const truncateMiddle = (str: string, length: number) => {
    if (str.length <= length) {
      return str
    }
    const frontLength = Math.ceil(length / 2)
    const backLength = Math.floor(length / 2)
    return `${str.substr(0, frontLength)}...${str.substr(str.length - backLength)}`
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }
    return date
      .toLocaleDateString('en-US', options)
      .replace(/, /g, ' ')
      .replace(/(\d{4})/, '$1,')
  }

  const getConfirmationUrl = () => {
    if (!orderDetails) return 'https://etherscan.io/'

    const { leftSideLabel, rightSideLabel } = orderDetails

    if (
      ['USDT-BSC', 'BUSD-BSC', 'USDC-BSC', 'USDT-B', 'BUSD-B'].includes(
        leftSideLabel
      ) ||
      ['USDT-BSC', 'BUSD-BSC', 'USDC-BSC', 'USDT-B', 'BUSD-B'].includes(
        rightSideLabel
      )
    ) {
      return 'https://bscscan.com/'
    } else if (
      ['USDC-POLY', 'CADC-POLY', 'USDC-P', 'CADC-P'].includes(leftSideLabel) ||
      ['USDC-POLY', 'CADC-POLY', 'USDC-P', 'CADC-P'].includes(rightSideLabel)
    ) {
      return 'https://polygonscan.com/'
    } else if (
      ['USDC-ARB', 'CADC-ARB'].includes(leftSideLabel) ||
      ['USDC-ARB', 'CADC-ARB'].includes(rightSideLabel)
    ) {
      return 'https://arbiscan.io/'
    } else if (
      ['USDC-ETH', 'CADC-ETH'].includes(leftSideLabel) ||
      ['USDC-ETH', 'CADC-ETH'].includes(rightSideLabel)
    ) {
      return 'https://etherscan.io/'
    } else if (leftSideLabel === 'USDC-OP' || rightSideLabel === 'USDC-OP') {
      return 'https://optimistic.etherscan.io/'
    } else if (
      leftSideLabel === 'USDC-AVAX' ||
      rightSideLabel === 'USDC-AVAX'
    ) {
      return 'https://snowtrace.io/'
    } else if (leftSideLabel === 'USDC-FTM' || rightSideLabel === 'USDC-FTM') {
      return 'https://ftmscan.com/'
    } else if (leftSideLabel === 'USDC-GNO' || rightSideLabel === 'USDC-GNO') {
      return 'https://gnosisscan.io/'
    } else if (
      leftSideLabel === 'USDC-CELO' ||
      rightSideLabel === 'USDC-CELO'
    ) {
      return 'https://celoscan.io/'
    } else if (leftSideLabel === 'USDC-SX' || rightSideLabel === 'USDC-SX') {
      return 'https://explorerl2.sx.technology/'
    } else if (
      ['USDC-BASE', 'CADC-BASE'].includes(leftSideLabel) ||
      ['USDC-BASE', 'CADC-BASE'].includes(rightSideLabel)
    ) {
      return 'https://basescan.org/'
    } else if (
      ['USDC-SOL', 'CADC-SOL'].includes(leftSideLabel) ||
      ['USDC-SOL', 'CADC-SOL'].includes(rightSideLabel)
    ) {
      return 'https://solscan.io/'
    } else {
      return 'https://etherscan.io/'
    }
  }

  const handleTransferError = (error: Error) => {
    const missingTokenAccountMatch = error?.message?.match(
      /^Sender (\w+) token account not found$/
    )
    if (missingTokenAccountMatch) {
      setError(
        t('orderStatus.errors.noTokenAccount', {
          token: missingTokenAccountMatch[1],
        })
      )
    } else if (
      error?.message ==
      'The requested method and/or account has not been authorized by the user.'
    ) {
      setError(t('orderStatus.errors.wrongAccount'))
    } else if (error?.message == 'User rejected the request.') {
      setError(t('orderStatus.errors.userRejected'))
    } else {
      setError(t('orderStatus.errors.unableToProcess'))
    }
  }

  const handleTransferComplete = () => {
    if (transactionId) {
      fetchOrderDetails(transactionId)
    }
  }

  const confirmationUrl = getConfirmationUrl()

  const getStoredTransactions = (): Record<string, StoredTransaction> => {
    try {
      const stored = localStorage.getItem(TRANSACTION_LISTENER_STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('Error reading stored transactions:', error)
      return {}
    }
  }

  const copyToClipboard = (text: string) => {
    try {
      navigator.clipboard.writeText(text).then(
        () => {},
        () => {
          fallbackCopyToClipboard(text)
        }
      )
    } catch {
      fallbackCopyToClipboard(text)
    }
  }

  const fallbackCopyToClipboard = (text: string) => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    document.body.appendChild(textarea)
    textarea.select()
    try {
      document.execCommand('copy')
    } catch {
      alert(t('orderStatus.copyFailed'))
    }
    document.body.removeChild(textarea)
  }

  return (
    <WidgetLayout
      showInteracLabel={false}
      showPoweredByLabel={true}
      heightClassName="h-[850px]"
    >
      {window.self === window.top && parent !== 'landing' && (
        <div
          className="absolute top-10 right-0"
          data-testid="container-order-status-header"
        >
          <DarkModeMenu showSignOutButton={true} />
        </div>
      )}
      <div
        className="order-status-container flex items-center justify-center"
        data-testid="container-order-status"
      >
        <div
          className="w-full max-w-4xl px-4 dark:text-white"
          style={{
            position: 'absolute',
            top: window.self === window.top ? '10%' : '9%',
            width: '100%',
          }}
          data-testid="container-order-status-content"
        >
          <h2
            className="text-lg font-normal mb-6 text-center text-black dark:text-white mt-12"
            data-testid="text-order-status-title"
          >
            {t('orderStatus.title')}
          </h2>
          {error && (
            <div
              className="flex flex-col items-center justify-center px-4 sm:px-6"
              data-testid="error-order-status"
            >
              <div className="w-full max-w-md text-center">
                <div className=" p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    {error === 'notFound'
                      ? t('orderStatus.notFound.title')
                      : t('orderStatus.failedToFetch')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {t('orderStatus.notFound.description')}
                  </p>
                </div>
              </div>
            </div>
          )}
          {orderDetails ? (
            <div
              className="px-4 sm:px-6"
              data-testid="container-order-status-details"
            >
              <h3
                className="text-lg font-semibold mb-4 text-center text-black dark:text-white"
                data-testid="text-order-status-status"
              >
                {getStatusLabel(
                  orderDetails.status,
                  orderDetails.paymentId ?? undefined
                )}
              </h3>
              {orderDetails.status?.toLowerCase().includes('pending') ||
              (orderDetails.status?.toLowerCase().includes('waiting') &&
                orderDetails.leftSideLabel === 'CAD') ? (
                <div
                  className="flex items-center justify-center mb-10 pt-10 pb-10"
                  style={{ height: '3rem' }}
                  data-testid="container-order-status-interac"
                >
                  <div className="w-full flex justify-center">
                    <a
                      className="bg-black text-white dark:bg-semiWhite dark:text-black py-8 px-4 border-black w-full border-2 items-center text-center"
                      href={`https://etransfer.interac.ca/reqPayment/eml/${orderDetails.rmtId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-order-status-interac"
                    >
                      {t('orderStatus.payWith')}
                      <img
                        src={InteracLogo}
                        alt="Interac Logo"
                        className="inline-block ml-2"
                        style={{ width: '24px', height: '24px' }}
                        data-testid="img-order-status-interac-logo"
                      />
                    </a>
                  </div>
                </div>
              ) : null}

              {orderDetails.leftSideLabel != 'CAD' &&
                depositAddress &&
                (orderDetails.status?.toLowerCase().includes('pending') ||
                  orderDetails.status?.toLowerCase().includes('waiting')) && (
                  <div
                    className="mb-8 sm:mb-10"
                    data-testid="container-order-status-wallet"
                  >
                    <div
                      className={`grid grid-cols-1 gap-4 items-center ${canSendFromWallet ? 'sm:grid-cols-[1fr_auto_1fr]' : ''}`}
                    >
                      {canSendFromWallet && (
                        <>
                          {/* Wallet payment button */}
                          <div
                            className="border-none border-gray-200 dark:border-gray-700 rounded-none"
                            data-testid="container-order-status-wallet-button"
                          >
                            <HybridWalletConnectButton
                              amount={orderDetails.leftSideValue}
                              recipientAddress={depositAddress}
                              senderAddress={orderDetails.wallet}
                              tokenSymbol={orderDetails.leftSideLabel}
                              orderId={orderDetails.paymentId ?? ''}
                              onTransferComplete={handleTransferComplete}
                              onError={handleTransferError}
                            />
                          </div>

                          {/* OR divider */}
                          <div
                            className="text-center mt-2"
                            data-testid="text-order-status-divider"
                          >
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 p-0">
                              {t('orderStatus.or')}
                            </span>
                          </div>
                        </>
                      )}

                      {/* Exchange payment button */}

                      <button
                        className="cursor-pointer"
                        onClick={() => setShowExchangeModal(true)}
                        data-testid="btn-order-status-exchange"
                      >
                        <a className="w-full py-6 bg-white  text-black dark:bg-black dark:text-white border-black text-center block">
                          <span> {t('orderStatus.payWithExchange')}</span>
                        </a>
                      </button>
                    </div>
                    {showLegacyCadcSellHint && (
                      <p
                        className="mt-4 text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400"
                        data-testid="text-order-status-legacy-cadc-hint"
                      >
                        {t('orderStatus.legacyCadcHint')}
                      </p>
                    )}
                  </div>
                )}
              <div
                className="space-y-2 px-4 sm:px-6"
                data-testid="container-order-status-info"
              >
                <div
                  className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                  data-testid="container-order-status-date"
                >
                  <span className="min-w-[100px] font-bold">
                    {t('orderStatus.date')}
                  </span>
                  <span
                    className="text-right ml-4 flex-grow"
                    data-testid="text-order-status-date"
                  >
                    {orderDetails.date ? formatDate(orderDetails.date) : '—'}
                  </span>
                </div>

                {orderDetails.status?.toLowerCase().includes('complete') &&
                  orderDetails.paymentId && (
                    <div
                      className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                      data-testid="container-order-status-confirmation"
                    >
                      <span className="font-semibold min-w-[100px]">
                        {t('orderStatus.confirmation')}
                      </span>
                      <span
                        className="text-right ml-4 flex-grow break-all"
                        data-testid="container-order-status-confirmation-link"
                      >
                        <a
                          href={`${confirmationUrl}tx/${orderDetails.paymentId}`}
                          rel="nofollow noopener noreferrer"
                          target="_blank"
                          className="text-blue-500 break-all"
                          data-testid="link-order-status-confirmation"
                        >
                          {truncateMiddle(orderDetails.paymentId, 20)}
                        </a>
                      </span>
                    </div>
                  )}

                <div
                  className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                  data-testid="container-order-status-txid"
                >
                  <span className="font-semibold min-w-[100px]">
                    {t('orderStatus.txId')}
                  </span>
                  <span
                    className="text-right ml-4 flex-grow break-all"
                    data-testid="text-order-status-txid"
                  >
                    {orderDetails.id}
                  </span>
                </div>

                <div
                  className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                  data-testid="container-order-status-pay"
                >
                  <span className="font-semibold min-w-[100px]">
                    {t('orderStatus.youPay')}
                  </span>
                  <span
                    className="text-right ml-4 flex-grow"
                    data-testid="text-order-status-pay"
                  >
                    {orderDetails.leftSideValue.toFixed(2)}{' '}
                    {orderDetails.leftSideLabel}
                  </span>
                </div>

                <div
                  className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                  data-testid="container-order-status-receive"
                >
                  <span className="font-semibold min-w-[100px]">
                    {t('orderStatus.youReceive')}
                  </span>
                  <span
                    className="text-right ml-4 flex-grow"
                    data-testid="text-order-status-receive"
                  >
                    {orderDetails.rightSideValue.toFixed(2)}{' '}
                    {orderDetails.rightSideLabel}
                  </span>
                </div>

                <div
                  className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                  data-testid="container-order-status-wallet"
                >
                  <span className="font-semibold min-w-[100px]">
                    {t(
                      isFiat(orderDetails.leftSideLabel)
                        ? 'orderStatus.sendToWallet'
                        : 'orderStatus.sendFromWallet'
                    )}
                  </span>
                  <span
                    className="ml-4 flex-grow break-all flex items-start justify-end gap-2"
                    data-testid="container-order-status-wallet-link"
                  >
                    <a
                      href={`${confirmationUrl}address/${orderDetails.wallet}#tokentxns`}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      className="text-blue-500 break-all"
                      data-testid="link-order-status-wallet"
                    >
                      {orderDetails.wallet}
                    </a>
                  </span>
                </div>

                {orderDetails.leftSideLabel != 'CAD' && depositAddress && (
                  <div
                    className="flex justify-between text-black dark:text-white text-xs sm:text-sm lg:text-base"
                    data-testid="container-order-status-receiving-wallet"
                  >
                    <span className="font-semibold min-w-[100px]">
                      {t('orderStatus.sendToPaytrieWallet')}
                    </span>
                    <span
                      className="ml-4 flex-grow break-all flex items-start justify-end gap-2"
                      data-testid="container-order-status-receiving-wallet-link"
                    >
                      <a
                        href={`${confirmationUrl}address/${depositAddress}`}
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                        className="text-blue-500 break-all"
                        data-testid="link-order-status-receiving-wallet"
                      >
                        {depositAddress}
                      </a>
                      <button
                        onClick={() => copyToClipboard(depositAddress)}
                        className="text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white transition-colors flex-shrink-0"
                        title="Copy address"
                        data-testid="btn-order-status-receiving-wallet-copy"
                      >
                        <CopyIcon />
                      </button>
                    </span>
                  </div>
                )}
              </div>

              {orderDetails.leftSideLabel != 'CAD' && (
                <div
                  className="mt-[72px] px-4 sm:px-6"
                  data-testid="container-order-status-wallet-notice"
                >
                  <div className="flex items-start gap-2 border border-gray-300 dark:border-gray-700 p-3 text-xs sm:text-sm text-black dark:text-white">
                    <ExclamationTriangleIcon
                      className="flex-shrink-0 mt-0.5"
                      width={18}
                      height={18}
                    />
                    <p>
                      <span className="font-semibold">
                        {t('orderStatus.walletNotice.important')}
                      </span>{' '}
                      {t('orderStatus.walletNotice.message')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            !error && (
              <p
                className="text-black dark:text-white"
                data-testid="text-order-status-loading"
              >
                {t('orderStatus.loading')}
              </p>
            )
          )}
        </div>
      </div>

      {/* Exchange Modal with responsive text */}

      {showExchangeModal && (
        <div
          className="fixed inset-0 bg-pebble bg-opacity-20 flex items-center justify-center z-50"
          data-testid="modal-order-status-exchange-overlay"
        >
          <div
            className="bg-white dark:bg-pebble p-8 max-w-md w-full rounded-none border border-gray-200 dark:border-gray-700"
            data-testid="container-order-status-exchange-modal"
          >
            <h3
              className="text-xl font-semibold mb-6 text-black dark:text-white"
              data-testid="text-order-status-exchange-title"
            >
              {t('orderStatus.exchangeModal.title')}
            </h3>
            <div
              className="space-y-6 text-gray-600 dark:text-gray-300 mb-6"
              data-testid="container-order-status-exchange-content"
            >
              {/* Amount */}
              <div
                className="space-y-2"
                data-testid="container-order-status-exchange-amount"
              >
                <p
                  className="font-semibold text-black dark:text-white"
                  data-testid="text-order-status-exchange-amount-label"
                >
                  {t('orderStatus.exchangeModal.amount')}
                </p>
                <div
                  className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 h-14"
                  data-testid="container-order-status-exchange-amount-field"
                >
                  <span
                    className="font-mono text-sm"
                    data-testid="text-order-status-exchange-amount"
                  >
                    {orderDetails?.leftSideValue} {orderDetails?.leftSideLabel}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(String(orderDetails?.leftSideValue ?? ''))
                    }
                    className="text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white transition-colors flex-shrink-0"
                    title="Copy amount"
                    data-testid="btn-order-status-exchange-amount-copy"
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>

              {depositAddress && (
                <>
                  {/* Send to Wallet */}
                  <div
                    className="space-y-2"
                    data-testid="container-order-status-exchange-wallet"
                  >
                    <p
                      className="font-semibold text-black dark:text-white"
                      data-testid="text-order-status-exchange-wallet-label"
                    >
                      {t('orderStatus.exchangeModal.sendToWallet')}
                    </p>
                    <div
                      className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 h-14"
                      data-testid="container-order-status-exchange-wallet-field"
                    >
                      <a
                        href={`${confirmationUrl}address/${depositAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-sm break-all hover:text-black dark:hover:text-white transition-colors truncate"
                        data-testid="link-order-status-exchange-wallet"
                      >
                        {depositAddress}
                      </a>
                      <button
                        onClick={() => {
                          copyToClipboard(depositAddress)
                          track('wallet_address_copied', {
                            tx_id: orderDetails?.paymentId ?? null,
                          })
                        }}
                        className="text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white transition-colors ml-2 flex-shrink-0"
                        title="Copy address"
                        data-testid="btn-order-status-exchange-wallet-copy"
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>

                  {/* QR Code (using SVG directly in the component) */}
                  <div
                    className="flex justify-center py-2"
                    data-testid="container-order-status-exchange-qr"
                  >
                    <div
                      className="bg-white p-2"
                      data-testid="img-order-status-exchange-qr"
                    >
                      <WalletAddressQRCode
                        address={depositAddress}
                        size={200}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Important Notes */}
              <div
                className="space-y-2"
                data-testid="container-order-status-exchange-notes"
              >
                <p
                  className="font-semibold text-black dark:text-white"
                  data-testid="text-order-status-exchange-notes-title"
                >
                  {t('orderStatus.exchangeModal.important')}
                </p>
                <div
                  className="space-y-2"
                  data-testid="container-order-status-exchange-notes-list"
                >
                  <p
                    className="text-sm"
                    data-testid="text-order-status-exchange-note-fees"
                  >
                    • {t('orderStatus.exchangeModal.includeFees.1')}{' '}
                    <span className="font-semibold">
                      {t('orderStatus.exchangeModal.includeFees.bold')}
                    </span>{' '}
                    {t('orderStatus.exchangeModal.includeFees.2')}
                  </p>
                  <p
                    className="text-sm"
                    data-testid="text-order-status-exchange-note-network"
                  >
                    •{' '}
                    {t('orderStatus.exchangeModal.onlyNetwork', {
                      token: orderDetails?.leftSideLabel?.split('-')[0],
                      network: orderDetails?.leftSideLabel?.split('-')[1],
                    })}
                  </p>
                  <p
                    className="text-sm"
                    data-testid="text-order-status-exchange-note-processing"
                  >
                    • {t('orderStatus.exchangeModal.processingTime')}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowExchangeModal(false)}
              className="w-full px-4 py-3 bg-black text-white dark:bg-white dark:text-black rounded-none hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors text-sm sm:text-base"
              data-testid="btn-order-status-exchange-close"
            >
              {t('orderStatus.exchangeModal.close')}
            </button>
          </div>
        </div>
      )}
    </WidgetLayout>
  )
}

export default OrderStatus
